import {
    Box,
    Button,
    Typography,
    FormControlLabel,
    Checkbox,
    CircularProgress,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import {
    Document,
    Page,
    pdfjs,
} from "react-pdf";
import { useTranslate } from "../../components/Translate/TranslateContext";
import { acceptTermsAndConditions } from "../../api/userRegistration";

pdfjs.GlobalWorkerOptions.workerSrc =
    new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
    ).toString();

function extractVersion(filename) {
    const match = filename.match(/(\d{4}_\d{2})/);
    return match ? match[1] : null;
}

export default function TermsAndConditions({
    user,
    onAccepted,
}) {
    const { translate, language } =
        useTranslate();
    const [checked, setChecked] =
        useState(false);

    const [loading, setLoading] =
        useState(false);

    const [pdf, setPdf] =
        useState(null);

    const [numPages, setNumPages] =
        useState(null);

    const [pdfLoading, setPdfLoading] =
        useState(true);

    const [hasScrolledToEnd, setHasScrolledToEnd] =
        useState(false);

    const [pageWidth, setPageWidth] =
        useState(900);

    const scrollContainerRef =
        useRef(null);

    const resizeObserverRef =
        useRef(null);

    const langCode =
        (
            language ||
            navigator.language ||
            "en"
        ).split("-")[0];

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(
                    "/legal/terms-manifest.json"
                );

                const manifest =
                    await res.json();

                const filesByLang =
                    manifest.files || {};

                const defaultLang =
                    manifest.default_language ||
                    "en";

                const candidateFiles =
                    filesByLang[langCode] ||
                    filesByLang[
                        defaultLang
                    ] ||
                    [];

                if (
                    !candidateFiles.length
                ) {
                    return;
                }

                const latestFile =
                    candidateFiles
                        .map((f) => ({
                            file: f,
                            version:
                                extractVersion(
                                    f
                                ),
                        }))
                        .filter(
                            (f) =>
                                f.version
                        )
                        .sort((a, b) =>
                            b.version.localeCompare(
                                a.version
                            )
                        )[0]?.file;

                if (latestFile) {
                    setPdf(
                        `/legal/${latestFile}`
                    );
                }
            } catch (err) {
                console.error(
                    "Failed to load terms manifest",
                    err
                );
            }
        })();
    }, [langCode]);

    useEffect(() => {
        const container =
            scrollContainerRef.current;

        if (!container) {
            return;
        }

        const updateWidth = () => {
            setPageWidth(
                Math.min(
                    container.clientWidth -
                        48,
                    950
                )
            );
        };

        updateWidth();

        resizeObserverRef.current =
            new ResizeObserver(() => {
                updateWidth();
            });

        resizeObserverRef.current.observe(
            container
        );

        return () => {
            resizeObserverRef.current?.disconnect();
        };
    }, [pdf]);

    const handleScroll = () => {
        const container =
            scrollContainerRef.current;

        if (!container) {
            return;
        }

        const reachedBottom =
            container.scrollTop +
                container.clientHeight >=
            container.scrollHeight -
                32;

        if (reachedBottom) {
            setHasScrolledToEnd(
                true
            );
        }
    };

    const handleDocumentLoadSuccess =
        ({ numPages }) => {
            setNumPages(
                numPages
            );

            setPdfLoading(
                false
            );
        };

    const handleAccept =
        async () => {
            if (
                !checked ||
                !hasScrolledToEnd ||
                !pdf
            ) {
                return;
            }

            setLoading(true);

            try {
                await acceptTermsAndConditions(
                    {
                        userId:
                            user.user_id,
                        version:
                            pdf
                                .split(
                                    "/"
                                )
                                .pop(),
                    }
                );

                onAccepted(
                    "email_verification"
                );
            } finally {
                setLoading(
                    false
                );
            }
        };

    if (!pdf) {
        return null;
    }

    return (
        <Box
            sx={{
                px: {
                    xs: 3,
                    md: 6,
                },
                py: 6,
                maxWidth: 1200,
                mx: "auto",
                display: "flex",
                flexDirection:
                    "column",
                gap: 3,
            }}
        >
            <Box>
               
                <Typography
                    variant="body2"
                    sx={{
                        mt: 1,
                        color:
                            "var(--color-text-primary)",
                        opacity: 0.75,
                    }}
                >
                    {translate(
                        "terms_conditions_description"
                    )}
                </Typography>
            </Box>

            <Box
                ref={
                    scrollContainerRef
                }
                onScroll={
                    handleScroll
                }
                sx={{
                    height: 650,
                    overflowY:
                        "auto",
                    border:
                        "1px solid var(--color-green-soft)",
                    borderRadius: 3,
                    backgroundColor:
                        "var(--color-bg-primary)",
                    boxShadow:
                        "0 2px 10px rgba(0,0,0,0.05)",
                    p: 3,
                }}
            >
                {pdfLoading && (
                    <Box
                        sx={{
                            height:
                                "100%",
                            display:
                                "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "center",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                <Box
                    sx={{
                        display:
                            "flex",
                        flexDirection:
                            "column",
                        alignItems:
                            "center",
                        gap: 2,
                    }}
                >
                    <Document
                        file={pdf}
                        onLoadSuccess={
                            handleDocumentLoadSuccess
                        }
                         onLoadError={(error) => {
        console.error(
            "PDF load error:",
            error
        );
    }}
                        loading=""
                    >
                        {Array.from(
                            {
                                length:
                                    numPages ||
                                    0,
                            },
                            (
                                _,
                                index
                            ) => (
                                <Page
                                    key={`page-${index}`}
                                    pageNumber={
                                        index +
                                        1
                                    }
                                    width={
                                        pageWidth
                                    }
                                    renderTextLayer={
                                        false
                                    }
                                    renderAnnotationLayer={
                                        false
                                    }
                                />
                            )
                        )}
                    </Document>
                </Box>
            </Box>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={
                            checked
                        }
                        disabled={
                            !hasScrolledToEnd
                        }
                        onChange={(
                            e
                        ) =>
                            setChecked(
                                e
                                    .target
                                    .checked
                            )
                        }
                    />
                }
                label={translate(
                    "i_accept_terms"
                )}
            />

            <Button
                variant="contained"
                size="large"
                disabled={
                    !checked ||
                    !hasScrolledToEnd ||
                    loading
                }
                onClick={
                    handleAccept
                }
                sx={{
                    minWidth: 260,
                    alignSelf:
                        "flex-start",
                    backgroundColor:
                        "var(--color-green-main)",
                    "&:hover": {
                        backgroundColor:
                            "var(--color-green-strong)",
                    },
                }}
            >
                {translate(
                    "continue"
                )}
            </Button>
        </Box>
    );
}